import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { invoiceDisplayName, PricingStore } from "../s25.event.pricing.store";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { FormsModule } from "@angular/forms";
import { Bind } from "../../../../decorators/bind.decorator";
import { TelemetryService } from "../../../../services/telemetry.service";
import { isUndefined } from "../../../../util/core";
import { S25Promise } from "../../../../util/core/s25.promise";
import { S25Error } from "../../../../util/core/s25.error";
import { ReportService } from "../../../../services/report.service";
import { s25Resource } from "../../../../util/s25.resource";
import { EventService } from "../../../../services/event.service";
import { Report } from "../../../../pojo/Report";
import { Invoice } from "../../../../pojo/Pricing";

/** Prints the whole event rather than one invoice, as legacy Standard Pricing does. */
export const EventInvoiceOption = "eventInvoice";
export type PrintInvoiceSelection = Invoice | typeof EventInvoiceOption;

@Component({
    selector: "s25-ng-pricing-print-invoice",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25ButtonComponent, FormsModule],
    host: { role: "group", "aria-label": "Print invoice" },
    template: `
        <select class="cn-form__control" aria-label="Select invoice to print" [(ngModel)]="selection">
            <option [ngValue]="eventInvoiceOption">Event Invoice</option>
            @for (invoice of invoices(); track invoice.id) {
                <option [ngValue]="invoice">{{ displayName(invoice) }}</option>
            }
        </select>
        @if (orgs().length > 1) {
            <select class="cn-form__control" aria-label="Organization to print for" [(ngModel)]="selectedOrgId">
                <option [ngValue]="undefined">All Organizations</option>
                @for (org of orgs(); track org.itemId) {
                    <option [ngValue]="org.itemId">{{ org.itemName }}</option>
                }
            </select>
        }
        <s25-ng-button
            [type]="'primary'"
            [onClick]="printInvoice"
            [disabled]="!!disabledReason()"
            [disabledReason]="disabledReason()"
            (clickPromise)="focusPrintButton()"
        >
            @if (combineEvents()) {
                Print Related Events Invoice
            } @else {
                Print Default Invoice
            }
        </s25-ng-button>
    `,
    styles: `
        :host {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
        }

        .cn-form__control {
            min-width: 150px;
        }

        @media (max-width: 640px) {
            :host {
                flex-direction: column;
            }

            .cn-form__control,
            s25-ng-button {
                width: 100%;
            }
        }
    `,
})
export class S25PricingPrintInvoiceComponent {
    private readonly pricingStore = inject(PricingStore);
    private readonly printButton = viewChild.required(S25ButtonComponent, { read: ElementRef });

    protected readonly displayName = invoiceDisplayName;
    protected readonly eventInvoiceOption = EventInvoiceOption;

    readonly eventId = this.pricingStore.eventId;
    readonly invoices = this.pricingStore.displayedInvoices;
    readonly combineEvents = this.pricingStore.combineRelated;
    readonly orgs = computed(() => [...this.pricingStore.organizations(), ...this.pricingStore.unlistedBilledOrgs()]);

    /** The store keeps the last viewed invoice selected even while the Invoice Summary is showing. */
    readonly defaultToEventInvoice = input(false);
    readonly selection = linkedSignal<PrintInvoiceSelection>(() =>
        this.defaultToEventInvoice() ? EventInvoiceOption : (this.pricingStore.selectedInvoice() ?? EventInvoiceOption),
    );
    readonly selectedOrgId = signal<number | undefined>(undefined);
    private readonly isEventInvoice = computed(() => this.selection() === EventInvoiceOption);

    private readonly reports = s25Resource({
        if: () => !!this.eventId(),
        params: () => this.eventId(),
        loader: ({ params: eventId }) => EventService.getEventReports(eventId),
    });

    readonly printReport = computed(() => {
        const reports = this.reports.value();
        if (!reports) return undefined;
        if (this.combineEvents()) return reports.find((report) => report.rpt_id === Report.Reports.InvoiceRelated.id);
        const reportUse = this.isEventInvoice() ? Report.Use.Invoice : Report.Use.Payment;
        return reports.find((report) => report.rpt_use === reportUse);
    });

    protected readonly disabledReason = computed(() => {
        if (this.reports.isLoading()) return "Loading invoice reports…";
        if (this.reports.error()) return "Invoice reports could not be loaded";
        if (this.printReport()) return undefined;
        return this.combineEvents()
            ? `"Event Invoice (Related)" is not associated with this event type`
            : "No default invoice configured on event type";
    });

    @Bind
    async printInvoice() {
        const selection = this.selection();
        const isEventInvoice = selection === EventInvoiceOption;
        TelemetryService.sendWithSub("Pricing", "Event", isEventInvoice ? "PrintEventInvoice" : "PrintInvoice");
        const eventId = this.eventId();
        const report = this.printReport();
        if (isUndefined(eventId) || !report) return;
        const evBillId = isEventInvoice ? undefined : selection.id;
        const [_, error] = await S25Promise.Maybe(
            ReportService.invoiceReport(eventId, evBillId, report, this.selectedOrgId()),
        );
        if (error) S25Error.show(error);
    }

    /**
     * The button is disabled while it prints, which drops focus to the page. It emits `clickPromise` before
     * re-rendering as enabled, so focus has to wait for that render.
     */
    protected focusPrintButton() {
        queueMicrotask(() => this.printButton().nativeElement.querySelector("button")?.focus());
    }
}
